import {Injectable} from '@angular/core';
import {logger} from './logger.service';

@Injectable({
	            providedIn: 'root'
            })
export class StorageService {

	private readonly OBFUSCATION_KEY = 'slaveous';
	private readonly isStorageAvailable = (): boolean => typeof localStorage !== 'undefined';

	public saveData<T>(uuid: string, value: T | null, className: string): void {
		if (!this.isStorageAvailable()) {
			return;
		}
		const existingData = this.getAllData(className) || {};
		localStorage.setItem(className,
		                     this.encodeString(JSON.stringify({
			                                                  ...existingData,
			                                                  [uuid]: value
		                                                  }))
		);
	}

	public getData<T>(uuid: string, className: string): T | undefined {
		const allData = this.getAllData(className);
		if (!allData) {
			return undefined;
		}
		return allData[uuid] as T | undefined;
	}

	public get(key: string) {
		if (!this.isStorageAvailable()) {
			return null;
		}
		return localStorage.getItem(key);
	}

	public set(key: string, value: string | null) {
		if (!this.isStorageAvailable()) {
			return;
		}
		if (value !== null) {
			localStorage.setItem(key, String(value));
		} else {
			localStorage.removeItem(key);
		}
	}

	private getAllData(className: string): Record<string, unknown> | undefined {
		if (!this.isStorageAvailable()) {
			return undefined;
		}
		if (localStorage.getItem(className)) {
			try {
				return JSON.parse(this.decodeString(localStorage.getItem(className)!)) as Record<string, unknown>;
			} catch (error) {
				logger.error(error);
				return undefined;
			}
		}
		return undefined;
	}

	saveAllData(objectList: Array<{ uuid: string }>, className: string): void {
		if (!this.isStorageAvailable()) {
			return;
		}
		localStorage.setItem(className, this.encodeString(JSON.stringify(Object.fromEntries(objectList.map(o => [o.uuid, o])))));
	}

	clear() {
		if (!this.isStorageAvailable()) {
			return;
		}
		return localStorage.clear();
	}

	private encodeString(data: string): string {
		const bytes = new TextEncoder().encode(data);
		const obfuscated = bytes.map((byte, index) => byte ^ this.OBFUSCATION_KEY.charCodeAt(index % this.OBFUSCATION_KEY.length));
		return btoa(String.fromCharCode(...obfuscated));
	}

	private decodeString(data: string): string {
		const bytes = Uint8Array.from(atob(data), c => c.charCodeAt(0));
		const deobfuscated = bytes.map((byte, index) => byte ^ this.OBFUSCATION_KEY.charCodeAt(index % this.OBFUSCATION_KEY.length));
		return new TextDecoder().decode(deobfuscated);
	}
}
